import { IconWithTooltip } from "@components/IconWithTooltip";
import { useModal } from "@components/Modal";
import { ArrowUpCircleIcon, PencilSquareIcon } from "@heroicons/react/16/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ChatMessageDTO } from "@onetutor/shared/dto";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface ChatInputBarProps {
  handleSubmit: (input: string) => void;
  isStreaming: boolean;
  isFetchingChatHistory: boolean;
  newChatPressed: () => void;
  messages: ChatMessageDTO[];
}

const DEFAULT_LINE_HEIGHT = 24;
const MAX_TEXTAREA_ROWS = 6;

export function ChatInputBar({
  handleSubmit,
  isStreaming,
  isFetchingChatHistory,
  newChatPressed,
  messages,
}: ChatInputBarProps) {
  const [input, setInput] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useTranslation(["chatPage"]);

  const { Modal: ChatInfoModal, openModal: openChatinfoModal } = useModal();

  useEffect(() => {
    if (messages.length === 0 && !isStreaming && !isFetchingChatHistory) {
      textareaRef.current?.focus();
    }
  }, [messages.length, isStreaming, isFetchingChatHistory]);

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    handleSubmit(input);
    setInput("");
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const scrollHeight = textareaRef.current.scrollHeight;

      const lineHeight =
        Number.parseInt(
          window.getComputedStyle(textareaRef.current).lineHeight,
        ) || DEFAULT_LINE_HEIGHT;
      const maxHeight = lineHeight * MAX_TEXTAREA_ROWS;

      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isStreaming && !isFetchingChatHistory) {
        const form = e.currentTarget.form;
        form?.requestSubmit();
      }
    }
  };

  return (
    <>
      <form
        onSubmit={handleFormSubmit}
        className="rounded-3xl bg-neutral p-3 px-3 py-2 sm:rounded-4xl sm:px-5 sm:py-5"
      >
        <textarea
          ref={textareaRef}
          value={input}
          required
          minLength={2}
          maxLength={5000}
          rows={1}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="w-full resize-none overflow-y-auto px-1 text-area text-area-ghost"
          placeholder={t("chatInputBar.inputPlaceholderChat")}
        />

        <div className="flex justify-between gap-2">
          <IconWithTooltip
            icon={<InformationCircleIcon className="h-5 w-5 sm:h-6 sm:w-6" />}
            tooltip={t("chatInputBar.tooltipChatInfo")}
            handler={() => openChatinfoModal()}
            className="btn btn-ghost btn-sm btn-circle"
          />
          <div className="flex gap-2">
            <IconWithTooltip
              icon={<PencilSquareIcon className="h-5 w-5 sm:h-6 sm:w-6" />}
              tooltip={t("chatInputBar.tooltipNewChat")}
              handler={newChatPressed}
              className="btn btn-ghost btn-sm btn-circle"
              disabled={isStreaming || messages.length === 0}
            />
            <IconWithTooltip
              icon={<ArrowUpCircleIcon className="h-6 w-6 sm:h-8 sm:w-8" />}
              tooltip={t("chatInputBar.send")}
              className="btn btn-ghost btn-sm btn-circle"
              disabled={isStreaming || isFetchingChatHistory}
              type="submit"
            />
          </div>
        </div>
      </form>
      <ChatInfoModal title={t("chatInfoModalTitle")}>
        <div>
          <p>{t("chatInfoModalContent")}</p>
          <h2 className="mt-3 font-bold text-lg">{t("chat")}</h2>
          <p>{t("chatInfoModalContentChat")}</p>
          <h2 className="mt-2 font-bold text-lg">
            {t("chatInfoConversationNotice")}
          </h2>
          <p>{t("chatInfoConversationNoticeText")}</p>
          <p className="mt-2 font-bold">{t("chatInfoModalContentFeedback")}</p>
        </div>
      </ChatInfoModal>
    </>
  );
}
